import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './UsageView.module.scss';
import SectionsCard from '../Sections/SectionsCard';
import SectionsQuizCard from '../Sections/SectionsQuizCard';
import { auGettingStartedGradeLevels, newGettingStartedGradeLevels } from '@src/constants';
import { SelectOption } from '@src/types';
import { 
  LessonByCourse,
  LessonsByCompetency,
  useGetLessonsByGradeLazyQuery,
  // useGetLessonsCompletedLazyQuery,
  useGetSurveySubmittedLazyQuery,
} from '@src/api/graphql-codegen';
import '../engagementTable.css'
import GradeColumnView from './GradeColumnView';
import CompetencyRating from './CompetencyRating';
import IconSlide from '../assets/presentation.svg?react';
// import EducatorWellBeingCoursesTable from './EducatorWellBeingCoursesTable';
// import ResilientClassroomsTable from './ResilientClassroomsTable';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import DetailListTable from './detailListTable/DetailListTable';
import { Collapse } from '@src/utils/componentCasts';

type Props = {
  isSuperAdmin?: boolean;
  selectedSchools?: SelectOption[];
  grade?: SelectOption;
  startDate?: string;
  endDate?: string;
  dateRange?: any[];
  datePickerVisible?: boolean;
  school?: SelectOption;
  isAussie?: boolean;
  isDistrictAdmin?: boolean;
  allSchoolOptions?: SelectOption[];
  filterSchools: string[];
  filterGrades: string[];
}

const noUsageFakeGrade = [
  {
    id: 0,
    "grade": "Grade 5",
    "lessonsCompleted": 80,
    "progress": 80,
    "__typename": "LessonByCourse"
  },
  {
    id: 1,
    "grade": "Grade 6",
    "lessonsCompleted": 50,
    "progress": 50,
    "__typename": "LessonByCourse"
  },
  {
    id: 2,
    "grade": "Grade 7",
    "lessonsCompleted": 30,
    "progress": 30,
    "__typename": "LessonByCourse"
  },
]

function UsageView(props: Props) {
  const {
    startDate='',
    endDate='',
    datePickerVisible,
    isSuperAdmin = false,
    selectedSchools = [],
    isAussie = false,
    isDistrictAdmin = false,
    filterSchools,
    filterGrades,
  } = props;
  const [selected, setSelected] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openPanel, setOpenPanel] = useState(false);
  const detailViewRef = useRef<HTMLDivElement>(null);
  const detailViewSecondRef = useRef<HTMLDivElement>(null);
  const [openExitTicket, setOpenExitTicket] = useState(false);
  const [openPreprogramQuiz, setOpenPreprogramQuiz] = useState(false);

  const handleSelect = useCallback((program: LessonByCourse, index: number) => {
    if (selectedId === `${index}-${program?.grade}`) {
      setSelected(false)
      setSelectedId(null);
    } else {
      setSelected(true)
      setSelectedId(`${index}-${program.grade}`);
      setTimeout(() => {
        if (program.grade) {
          const courseElement = document.getElementById(program.grade);
          courseElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          })
        }
      }, 500)
    }
  }, [selectedId, setSelectedId])

  const handleOpenCard = () => {
    if (!openPanel) {
      if (detailViewRef.current) {
        detailViewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      setOpenPreprogramQuiz(false);
      setOpenExitTicket(false);
    }
    setOpenPanel(current => !current)
  }

  const handleOpenExitTicket = () => {
    if (!openExitTicket) {
      setTimeout(() => {
        if (detailViewSecondRef.current) {
          detailViewSecondRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 500);
      setOpenExitTicket(true);
    } else {
      setOpenExitTicket(false);
    }
    setOpenPreprogramQuiz(false);
  }

  const handleOpenPreprogramQuiz = () => {
    if (!openPreprogramQuiz) {
      setTimeout(() => {
        if (detailViewSecondRef.current) {
          detailViewSecondRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 500);
      setOpenPreprogramQuiz(true);
    } else {
      setOpenPreprogramQuiz(false);
    }
    setOpenExitTicket(false);
  }

  const handleCloseQuiz = () => {
    setOpenExitTicket(false);
    setOpenPreprogramQuiz(false);
  }

  const [loadLessonsByGrade, {data: clbg, loading}] = useGetLessonsByGradeLazyQuery({ fetchPolicy: 'cache-first' })
  const [loadExitTicketSubmitted, {data: submittedData, loading: submittedLoading}] = useGetSurveySubmittedLazyQuery({ fetchPolicy: 'cache-first' })

  useEffect(() => {
    if (filterGrades && filterSchools.length && !datePickerVisible) {
      loadLessonsByGrade({
        variables: {
          input: {
            fromDate: startDate,
            toDate: endDate,
            schoolIds: filterSchools,
            ageGroups: filterGrades,
            limit: -1,
            offset: 0
          }
        }
      })
      loadExitTicketSubmitted({
        variables: {
          input: {
            fromDate: startDate,
            toDate: endDate,
            schoolIds: filterSchools,
            ageGroups: filterGrades,
          }
        }
      })
    }
  }, [
    datePickerVisible,
    startDate,
    endDate,
    filterGrades,
    filterSchools,
    isDistrictAdmin,
    isSuperAdmin,
    selectedSchools.length,
    loadExitTicketSubmitted,
    loadLessonsByGrade,
  ])

  const noUsageData = useMemo(() => {
    if (clbg?.lessonsByGrade?.byGrade?.total === 0 && (submittedData?.surveySubmitted?.exitTicket?.total === 0 || submittedData?.surveySubmitted?.exitTicket?.total === null) && (submittedData.surveySubmitted.preProgramQuiz?.total === 0 || submittedData.surveySubmitted.preProgramQuiz?.total === null)) {
      return true;
    }
    return false;
  }, [clbg?.lessonsByGrade?.byGrade?.total, submittedData?.surveySubmitted])

  const completedLessonsByGrade = useMemo(() => {
    if (noUsageData) {
      return noUsageFakeGrade;
    }
    return clbg?.lessonsByGrade?.byGrade?.data?.map((lessonData, index) => {
      return {
        id: index,
        ...lessonData
      }
    }).sort((a, b) => {
      if (isAussie) {
        return auGettingStartedGradeLevels.indexOf(a?.grade ?? "") - auGettingStartedGradeLevels.indexOf(b?.grade ?? "");
      } else {
        return newGettingStartedGradeLevels.indexOf(a?.grade ?? "") - newGettingStartedGradeLevels.indexOf(b?.grade ?? "");
      }
    });
  }, [clbg, isAussie, noUsageData])

  const graphData = useMemo(() => {
    return clbg?.lessonsByGrade?.summary?.items ?? [];
  }, [clbg?.lessonsByGrade?.summary?.items])

  const completedLessons = useMemo(() => {
    if (!completedLessonsByGrade) return [];
    const result = completedLessonsByGrade.flatMap(course =>
      (course?.lessons ?? []).flatMap(lesson =>
        (lesson?.items ?? []).map(item => ({
          grade: course?.grade ?? '',
          completedAt: item?.completedAt ?? '',
          lessonName: item?.lessonName ?? '',
          lessonType: item?.lessonType ?? '',
          teacherName: lesson?.teacherName ?? '',
          unitName: item?.unitName ?? '',
        }))
      )
    );

    return result.sort((a, b) => {
      const dateA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
      const dateB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
      return dateB - dateA;
    });
  }, [completedLessonsByGrade])

  const completedSurveyLessons = useMemo(() => {
    if (!submittedData?.surveySubmitted) return [];
    
    const result = openExitTicket ? [...submittedData?.surveySubmitted.exitTicket?.items ?? []] : [...submittedData.surveySubmitted.preProgramQuiz?.items ?? []]

    return result.sort((a, b) => {
      const dateA = a?.dateCompleted ? Number(a.dateCompleted) : 0;
      const dateB = b?.dateCompleted ? Number(b.dateCompleted) : 0;
      return dateB - dateA;
    });
  }, [submittedData?.surveySubmitted, openExitTicket])

  return (
    <div className={styles.usageList}>
      {noUsageData && <div className={styles.noUsageBanner}>
        <div className={styles.subView}>
          <IconSlide className={styles.slideIcon} />
          <div className={styles.selectedTitle}>There is no usage to display.</div>
        </div>
      </div>}
      <div className={styles.sectionView}>
        {/* <TitleSection title="Student Lessons" leftIcon={<Icon src={IconClassRoomTop} />} /> */}
        {loading ?
          <div style={{padding: '0 30px'}}>
            <Skeleton height={20} highlightColor="#ffb073" />
          </div> :
        <div style={{display: 'flex', padding: '0', gap: '40px'}}>
          <SectionsCard
            titleLine1='Completed Lessons'
            titleLine2={`${clbg?.lessonsByGrade?.summary?.total ?? 0}`}
            loading={loading}
            graphData={graphData}
            isEducators
            width="100%"
            startDate={startDate}
            endDate={endDate}
            noData={noUsageData}
            isClickable={true}
            openCard={handleOpenCard}
          />
        </div>}
        <Collapse isOpened={openPanel && completedLessons.length > 0}>
          <div ref={detailViewRef} style={{margin: "0 60px"}}>
            <DetailListTable
              completedLessons={completedLessons}
              isAussie={isAussie}
              handleClose={handleOpenCard}/>
          </div>
        </Collapse>
      </div>
      {/* {(isDistrictAdmin || (isSuperAdmin && selectedSchools.length > 1)) && (
        <>
          <TitleSection title="Completed Lessons by Schools" leftIcon={<Icon src={IconInventory} />} />
          <div className={styles.lessonsView}>
            {
              lessonsBySchoolsDataLoading
              ? <div className={styles.spinner}><Spinner /></div>
              :  completedLessonsBySchool && completedLessonsBySchool.length > 0
                ?
                  <div>
                    {completedLessonsBySchool.map((grade) => (
                      <SchoolColumnView key={grade.id} data={grade}/>
                    ))}
                  </div>
                : <div className={styles.nodata}>No Data</div>
            }
          </div>
        </>
      )} */}
      {/* Completed Lessons by Grade */}
      <div className={styles.sectionSubView}>
        <div className={styles.lessonsView} style={{filter: noUsageData ? 'blur(5px)' : ''}}>
          {
            loading
            ? <div style={{padding: '30px 0'}}><Skeleton height={20} highlightColor="#ffb073" /></div>
            : (completedLessonsByGrade && completedLessonsByGrade.length > 0
              ?
                <div>
                  {completedLessonsByGrade.map((grade, index) => (
                    <GradeColumnView
                      key={index}
                      gradeId={`${index}-${grade.grade}`}
                      grade={grade}
                      selected={selected}
                      selectedId={selectedId}
                      loading={loading}
                      handleSeleted={() => handleSelect(grade, index)}
                      isDistrictAdmin={isDistrictAdmin || (isSuperAdmin && selectedSchools.length > 1)}
                      isAussie={isAussie}
                      isLast={index === completedLessonsByGrade.length - 1}
                      noData={noUsageData}
                    />
                  ))}
                </div>
              : <div className={styles.nodata}>No Data</div>)
          }
        </div>
      </div>
      <div ref={detailViewSecondRef} style={{display: 'flex', padding: '0', gap: '40px', marginTop: 20}}>
        {submittedLoading ?
        <div style={{width: '100%', padding: '0'}}>
          <Skeleton height={20} highlightColor="#ffb073" />
        </div> :
        <SectionsQuizCard
          quizName='Pre-Program Quiz'
          submissions={submittedData?.surveySubmitted?.preProgramQuiz?.total ?? 0}
          result={submittedData?.surveySubmitted?.preProgramQuiz?.result ?? 0}
          loading={submittedLoading}
          isCardBlured={noUsageData || loading || submittedLoading || openExitTicket}
          openCard={handleOpenPreprogramQuiz}
        />}
        {submittedLoading ?
        <div style={{width: '100%', padding: '0'}}>
          <Skeleton height={20} highlightColor="#ffb073" />
        </div> :
        <SectionsQuizCard
          quizName='Exit Tickets'
          submissions={submittedData?.surveySubmitted?.exitTicket?.total ?? 0}
          result={submittedData?.surveySubmitted?.exitTicket?.result ?? 0}
          loading={submittedLoading}
          isCardBlured={noUsageData || loading || submittedLoading || openPreprogramQuiz}
          openCard={handleOpenExitTicket}
        />}
      </div>
      <Collapse isOpened={openExitTicket || openPreprogramQuiz}>
        <div style={{margin: "0 60px"}}>
          <DetailListTable
            isAussie={isAussie}
            isExitTicket={openExitTicket}
            isPreprogramQuiz={openPreprogramQuiz}
            completedLessons={completedSurveyLessons}
            handleClose={handleCloseQuiz}
          />
        </div>
      </Collapse>
      {!isAussie && <div className={styles.sectionView} style={{marginTop: 20}}>
        <div className={styles.sectionSubView}>
          <CompetencyRating 
            filterGrades={filterGrades}
            filterSchools={filterSchools}
            fromDate={startDate ?? ''}
            toDate={endDate ?? ''}
            noData={noUsageData || loading || submittedLoading}
            completedLessons={clbg?.lessonsByGrade?.byCompetency as LessonsByCompetency}
            totalCompleted={clbg?.lessonsByGrade?.summary?.total}
          />
        </div>
      </div>}
      {/* Comment out as we will reuse this elsewhere another time. */}
      {/* <div className={styles.sectionView}>
        <TitleSection title="Educator Lessons" leftIcon={<Icon src={IconClassRoomTop} />} />
        <div className={styles.sectionSubView}>
          <ResilientClassroomsTable
            filterGrades={filterGrades}
            filterSchools={filterSchools}
            fromDate={startDate ?? ''}
            toDate={endDate ?? ''}
            isDistrictAdmin={isDistrictAdmin || (isSuperAdmin && selectedSchools.length > 1)}
            isOk={!!filterGrades.length && !!filterSchools.length && !datePickerVisible}
          />
          <EducatorWellBeingCoursesTable
            filterGrades={filterGrades}
            filterSchools={filterSchools}
            fromDate={startDate ?? ''}
            toDate={endDate ?? ''}
            isDistrictAdmin={isDistrictAdmin || (isSuperAdmin && selectedSchools.length > 1)}
            isOk={!!filterGrades.length && !!filterSchools.length && !datePickerVisible}
          />
        </div>
      </div> */}
      </div>
  )
}

export default UsageView;
